import React from 'react';
import { ThumbsUp, ThumbsDown, Eye, CalendarDays, Trophy, Info, TrendingUp } from 'lucide-react';
import { bestSetOf, maxRpeOf, formatRelativeDateEs } from '../../../utils/progression';

/**
 * <ExerciseHeader /> — consolidated, compact header for the active workout.
 *
 * Replaces the old triple stack (header + LastSessionCard + BestSetCard)
 * with a single 2-column block:
 *
 *   ┌─────────┐  Exercise title
 *   │  image  │  Última sesión · 35 kg × 12 reps · RPE 9 · hace 4 semanas
 *   │ 80/112  │  Mejor marca · 35 kg × 12 reps · hace 4 meses
 *   └─────────┘
 *   👁 Técnica · 👍 · 👎
 *
 * Design rules:
 *   - Soft inline labels (lowercase, no tracking) — coaching tone, not dashboard.
 *   - Inline "·"-separated metadata, no fixed-width grid.
 *   - Image stays LEFT on every breakpoint to preserve horizontal reading flow.
 *   - 👁 Técnica is slightly more prominent than 👍/👎 (functional vs preference).
 *   - kg / reps / RPE are always explicit. Never compressed (e.g. no "35×12").
 */
const ExerciseHeader = ({
  imageUrl,
  name,
  trainerNotes,
  onOpenDetails,
  feedback,
  onToggleFeedback,
  hasInstructions = false,
  // Consolidated context (was three separate components)
  lastSession = null,
  pr = null,
  onOpenWorkout,
  onOpenProgress,
}) => {
  const toggle = (value) => {
    if (onToggleFeedback) onToggleFeedback(feedback === value ? null : value);
  };

  const bestLast = bestSetOf(lastSession?.sets);
  const lastRpe = maxRpeOf(lastSession?.sets);

  // Bodyweight-aware performance string: show "N reps" when there's no load.
  const fmtPerf = (w, r) => ((w && w > 0) ? `${w} kg × ${r} reps` : `${r} reps`);

  return (
    <div
      className="flex gap-3 sm:gap-4 mb-4"
      data-testid="intra-exercise-header"
    >
      {/* LEFT — image + actions */}
      <div className="flex flex-col items-start flex-shrink-0">
        {imageUrl ? (
          <button
            type="button"
            onClick={onOpenDetails}
            className="relative group focus:outline-none focus:ring-2 focus:ring-blue-400/50 rounded-lg"
            data-testid="intra-exercise-image"
            aria-label="Ver detalles del ejercicio"
          >
            <img
              src={imageUrl}
              alt={name}
              className="w-20 h-20 sm:w-28 sm:h-28 object-cover rounded-lg border border-white/10 group-hover:border-blue-400/40 transition-colors"
              loading="eager"
            />
          </button>
        ) : (
          <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-lg border border-white/5 bg-slate-900/40" />
        )}

        {/* Actions row — 👁 Técnica is slightly more prominent (functional);
            👍 / 👎 are smaller and muted (preference memory). */}
        <div className="flex items-center gap-1 mt-2" data-testid="intra-actions-row">
          {hasInstructions && (
            <button
              type="button"
              onClick={onOpenDetails}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-blue-200 bg-blue-500/10 border border-blue-400/25 hover:bg-blue-500/15 hover:border-blue-400/50 transition"
              data-testid="intra-action-technique"
              title="Técnica e instrucciones"
            >
              <Eye className="w-3.5 h-3.5" />
              <span>Técnica</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => toggle('favorite')}
            className={`p-1 rounded transition ${
              feedback === 'favorite'
                ? 'text-green-300'
                : 'text-slate-500 hover:text-green-300/70'
            }`}
            title="Me gusta este ejercicio"
            data-testid="intra-feedback-like"
            aria-pressed={feedback === 'favorite'}
          >
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => toggle('dislike')}
            className={`p-1 rounded transition ${
              feedback === 'dislike'
                ? 'text-red-300'
                : 'text-slate-500 hover:text-red-300/70'
            }`}
            title="No me gusta este ejercicio"
            data-testid="intra-feedback-dislike"
            aria-pressed={feedback === 'dislike'}
          >
            <ThumbsDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* RIGHT — title + metadata + suggestion */}
      <div className="flex-1 min-w-0">
        <h2
          className="text-xl sm:text-2xl font-bold text-white leading-tight tracking-tight"
          data-testid="intra-exercise-name"
        >
          {name}
        </h2>

        {trainerNotes && (
          <div className="mt-1.5 text-xs sm:text-sm text-blue-200/85 bg-blue-500/[0.06] border-l-2 border-blue-400/40 pl-2 py-1 rounded-r">
            <span className="text-blue-300/70 font-medium mr-1">Nota:</span>
            {trainerNotes}
          </div>
        )}

        {/* Metadata block — slight breathing room from title.
            Subtle layered depth: each row gets a faint tinted background +
            anchor icon + numeric weight. Coaching tone preserved (lowercase
            labels, no caps, no tracking). NOT a card stack. */}
        <div className="mt-3 space-y-1.5 text-sm leading-relaxed" data-testid="intra-meta">
          {/* Última sesión — slate tint, calendar anchor */}
          <MetaRow
            testid="intra-meta-last-session"
            label="Última sesión"
            icon={<CalendarDays className="w-3.5 h-3.5 text-slate-400/80" />}
            tone="slate"
            empty={!bestLast}
            emptyText="sin historial"
          >
            <span className="tabular-nums text-slate-50 font-medium">
              {fmtPerf(bestLast?.weight, bestLast?.reps)}
            </span>
            {lastSession?.workout_id && onOpenWorkout && (
              <button
                type="button"
                onClick={() => onOpenWorkout(lastSession.workout_id)}
                className="ml-1.5 px-1.5 py-0.5 rounded bg-slate-500/25 text-slate-100 text-[10px] font-semibold hover:bg-slate-400/40 active:scale-95 transition inline-flex items-center gap-0.5"
                data-testid="last-session-info-chip"
                title="Ver el entrenamiento completo de ese día"
              >
                <Info className="w-3 h-3" /> + info
              </button>
            )}
            {lastRpe != null && <Sep>·</Sep>}
            {lastRpe != null && <span className="text-slate-300">RPE {lastRpe}</span>}
            {lastSession?.date && <Sep>·</Sep>}
            {lastSession?.date && (
              <span className="text-slate-400">{formatRelativeDateEs(lastSession.date)}</span>
            )}
            {lastSession?.is_deload && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 text-[10px] font-medium">
                🪶 descarga
              </span>
            )}
          </MetaRow>

          {/* Mejor marca — amber tint, trophy anchor with soft glow */}
          <MetaRow
            testid="intra-meta-pr"
            label="Mejor marca"
            icon={
              <Trophy
                className="w-3.5 h-3.5 text-amber-300"
                style={{ filter: 'drop-shadow(0 0 4px rgba(252,211,77,0.45))' }}
              />
            }
            tone="amber"
            empty={!pr}
            emptyText="aún sin PR"
          >
            <span className="tabular-nums text-amber-50 font-medium">
              {fmtPerf(pr?.weight, pr?.reps)}
            </span>
            {pr?.workout_id && onOpenWorkout && (
              <button
                type="button"
                onClick={() => onOpenWorkout(pr.workout_id)}
                className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/25 text-amber-100 text-[10px] font-semibold hover:bg-amber-400/40 active:scale-95 transition inline-flex items-center gap-0.5"
                data-testid="pr-info-chip"
                title="Ver el entrenamiento donde se logró este PR"
              >
                <Info className="w-3 h-3" /> + info
              </button>
            )}
            {pr?.date && <Sep>·</Sep>}
            {pr?.date && (
              <span className="text-amber-200/60">{formatRelativeDateEs(pr.date)}</span>
            )}
          </MetaRow>
        </div>

        {/* Ver progreso — subtle entry to the full progression chart for this
            exercise. Only shown when there's history worth charting. Ideal to
            glance at during rest. */}
        {onOpenProgress && (lastSession || pr) && (
          <button
            type="button"
            onClick={onOpenProgress}
            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-cyan-200 bg-cyan-500/10 border border-cyan-400/25 hover:bg-cyan-500/20 hover:border-cyan-400/50 active:scale-95 transition"
            data-testid="intra-open-progress"
            title="Ver tu progreso en este ejercicio"
          >
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Ver progreso</span>
          </button>
        )}

      </div>
    </div>
  );
};

const Sep = ({ children }) => (
  <span className="text-slate-600 mx-1" aria-hidden="true">{children}</span>
);

const TONE_CLASSES = {
  slate: {
    bg: 'bg-white/[0.03] hover:bg-white/[0.05]',
    rail: 'border-l border-slate-500/30',
    label: 'text-slate-400',
  },
  amber: {
    bg: 'bg-amber-500/[0.06] hover:bg-amber-500/[0.09]',
    rail: 'border-l border-amber-400/40',
    label: 'text-amber-300/80',
  },
};

const MetaRow = ({ label, icon, tone = 'slate', empty, emptyText, children, testid }) => {
  const toneCls = TONE_CLASSES[tone] || TONE_CLASSES.slate;
  return (
    <div
      className={`flex flex-wrap items-center gap-x-1.5 pl-2 pr-2.5 py-1 rounded-md ${toneCls.bg} ${toneCls.rail} transition-colors`}
      data-testid={testid}
    >
      {icon && <span className="inline-flex shrink-0 mr-0.5">{icon}</span>}
      <span className={`${toneCls.label} font-normal`}>{label}</span>
      <Sep>·</Sep>
      {empty ? (
        <span className="text-slate-500 italic">{emptyText}</span>
      ) : (
        children
      )}
    </div>
  );
};

export default ExerciseHeader;
